<script setup lang="ts">
import type { MediaAlternative, MediaDetail } from '../../shared/contracts'
import { Bookmark, Calendar, Check, Clock3, RefreshCw } from '@lucide/vue'
import { useHead } from '@unhead/vue'
import { computed, onActivated, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PosterArt from '@/components/media/PosterArt.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import { preloadPlayerView } from '@/router'
import { api } from '@/services/api'
import { recallFreshMediaDetail, recallMediaPreview, rememberMediaDetail, rememberMediaSummary } from '@/services/mediaCache'
import { useAppStore } from '@/stores/app'
import { useContentSourcesStore } from '@/stores/contentSources'
import { useLibraryStore } from '@/stores/library'
import { episodeDisplayLabel } from '@/utils/episode'
import { safeReturnPath } from '@/utils/navigation'
import { MAX_ENABLED_SOURCES } from '../../shared/content-sources'

defineOptions({ name: 'DetailView' })

const route = useRoute()
const router = useRouter()
const library = useLibraryStore()
const appStore = useAppStore()
const contentSources = useContentSourcesStore()
const initialSourceId = String(route.params.source)
const initialMediaId = String(route.params.id)
const DETAIL_CACHE_MAX_AGE_MS = 60_000
const cachedDetail = recallFreshMediaDetail(initialSourceId, initialMediaId, DETAIL_CACHE_MAX_AGE_MS)
const detail = ref<MediaDetail | undefined>(cachedDetail?.data ?? recallMediaPreview(initialSourceId, initialMediaId))
const loading = ref(!detail.value)
const refreshing = ref(Boolean(detail.value && !cachedDetail))
const error = ref('')
const switching = ref('')
const enteringEpisode = ref<number>()
const alternativesLoading = ref(false)
let detailRequest = 0
let alternativesRequest = 0
let lastLoadedAt = cachedDetail?.updatedAt || 0
let activatedOnce = false
const favorite = computed(() => detail.value ? library.isFavorite(detail.value) : false)
const record = computed(() => detail.value ? library.getRecord(detail.value) : undefined)
const returnPath = computed(() => safeReturnPath(route.query.from))

useHead(computed(() => ({ title: detail.value?.title || '影片详情' })))

function comparableTitle(value: string) {
    return value.replace(/[\s·:：,，.。!！?？\-_]/gu, '').toLocaleLowerCase()
}

async function loadAlternatives() {
    if (!detail.value) { return }
    const requestId = ++alternativesRequest
    const mediaKey = `${detail.value.sourceId}:${detail.value.id}`
    alternativesLoading.value = true
    try {
        const title = detail.value.title
        const localIds = new Set(contentSources.sources.map(source => source.id))
        const remoteSources = appStore.activeSources.filter(source => source.searchable !== false && !localIds.has(source.id))
        const localSources = contentSources.sources.slice(0, Math.max(0, MAX_ENABLED_SOURCES - remoteSources.length))
        const requests = []
        remoteSources.slice(0, MAX_ENABLED_SOURCES).forEach(source => requests.push(api.search(title, source.id)))
        if (localSources.length) { requests.push(api.searchSources(title, localSources)) }
        const settled = await Promise.allSettled(requests)
        const matches = settled.flatMap(result => result.status === 'fulfilled' ? result.value.items : []).filter((item) => {
            const sameTitle = comparableTitle(item.title) === comparableTitle(title)
            const sameYear = !detail.value?.year || !item.year || detail.value.year === item.year
            return sameTitle && sameYear && !(item.sourceId === detail.value?.sourceId && item.id === detail.value.id)
        })
        matches.forEach(rememberMediaSummary)
        const unique = new Map(matches.map(item => [`${item.sourceId}:${item.id}`, item]))
        if (requestId === alternativesRequest && detail.value && `${detail.value.sourceId}:${detail.value.id}` === mediaKey) {
            detail.value.alternatives = Array.from(unique.values(), item => ({ id: item.id, sourceId: item.sourceId, sourceName: item.sourceName, episodeCount: item.episodeCount }))
        }
    }
    finally {
        if (requestId === alternativesRequest) { alternativesLoading.value = false }
    }
}

async function load(background = false) {
    const requestId = ++detailRequest
    alternativesRequest++
    alternativesLoading.value = false
    if (background && detail.value) {
        refreshing.value = true
        detail.value = { ...detail.value, alternatives: undefined, episodes: [] }
    }
    else { loading.value = true }
    error.value = ''
    try {
        await appStore.initialize()
        await contentSources.initialize()
        const sourceId = initialSourceId
        const localSource = contentSources.findSource(sourceId)
        const response = localSource
            ? await api.detailFromSource(localSource, initialMediaId)
            : await api.detail(sourceId, initialMediaId)
        if (requestId !== detailRequest) { return }
        detail.value = response
        lastLoadedAt = Date.now()
        rememberMediaDetail(response)
        void loadAlternatives()
    }
    catch (caught) {
        if (requestId === detailRequest) { error.value = caught instanceof Error ? caught.message : '详情加载失败' }
    }
    finally {
        if (requestId === detailRequest) {
            loading.value = false
            refreshing.value = false
        }
    }
}

async function play(index: number) {
    if (enteringEpisode.value !== undefined) { return }
    enteringEpisode.value = index
    try {
        await preloadPlayerView()
        await router.push({
            name: 'play',
            params: { source: initialSourceId, id: initialMediaId, episode: index },
            query: { from: returnPath.value },
        })
    }
    finally {
        enteringEpisode.value = undefined
    }
}

async function switchSource(alternative: MediaAlternative) {
    switching.value = `${alternative.sourceId}:${alternative.id}`
    try {
        await router.replace({ name: 'detail', params: { source: alternative.sourceId, id: alternative.id }, query: route.query })
    }
    finally {
        switching.value = ''
    }
}

onMounted(() => {
    void preloadPlayerView()
    if (cachedDetail) { void loadAlternatives() }
    else { void load(Boolean(detail.value)) }
})
onActivated(() => {
    if (!activatedOnce) {
        activatedOnce = true
        return
    }
    if (Date.now() - lastLoadedAt > DETAIL_CACHE_MAX_AGE_MS) { void load(true) }
})
</script>

<template>
    <div v-if="loading && !detail" class="min-h-screen animate-pulse">
        <div class="grid grid-cols-[minmax(0,1fr)_minmax(116px,38vw)] mx-auto max-w-[1480px] min-h-[260px] items-center gap-5 px-5 py-10 lg:grid-cols-[minmax(0,1fr)_340px] sm:grid-cols-[minmax(0,1fr)_minmax(190px,32vw)] lg:min-h-[620px] sm:min-h-[520px] lg:gap-20 sm:gap-10 lg:px-12 lg:py-16 sm:px-8 sm:py-14">
            <div class="min-w-0">
                <div class="h-3.5 w-24 rounded-full bg-[var(--c-skeleton)]" />
                <div class="mt-6 h-14 max-w-xl w-4/5 rounded-lg bg-[var(--c-skeleton)] sm:h-20" />
                <div class="mt-6 h-4 max-w-sm w-2/3 rounded-full bg-[var(--c-skeleton)]" />
            </div>
            <div class="aspect-[2/3] w-full rounded-[1.375rem] bg-[var(--c-skeleton)] sm:rounded-[1.75rem]" />
        </div>
        <div class="page-shell">
            <div class="h-8 w-1/3 bg-[var(--c-skeleton)]" />
        </div>
    </div>
    <div v-else-if="error && !detail" class="page-shell">
        <EmptyState title="影片详情加载失败" :description="error">
            <button class="mt-5 btn-primary" @click="load()">
                <RefreshCw class="size-4" />重新加载
            </button>
        </EmptyState>
    </div>
    <article v-else-if="detail" class="min-h-screen min-w-0 overflow-hidden">
        <section class="border-b border-[var(--c-border)] bg-[var(--c-canvas)]">
            <div class="grid grid-cols-[minmax(0,1fr)_minmax(116px,38vw)] mx-auto max-w-[1480px] min-h-[260px] items-center gap-5 px-5 py-10 lg:grid-cols-[minmax(0,1fr)_340px] sm:grid-cols-[minmax(0,1fr)_minmax(190px,32vw)] lg:min-h-[620px] sm:min-h-[520px] lg:gap-20 sm:gap-10 lg:px-12 lg:py-16 sm:px-8 sm:py-14">
                <div class="reveal-up max-w-3xl min-w-0">
                    <p class="mb-4 text-xs text-[var(--accent)] font-700 sm:mb-5 sm:text-sm">
                        {{ detail.sourceName }}
                    </p>
                    <h1 class="[overflow-wrap:anywhere] max-w-[14ch] break-words text-balance text-[clamp(2rem,6vw,4.75rem)] text-[var(--c-text)] font-400 leading-[1.02] font-editorial">
                        {{ detail.title }}
                    </h1>
                    <p v-if="detail.originalTitle" class="mt-3 truncate text-sm text-[var(--c-text-muted)] sm:text-base">
                        {{ detail.originalTitle }}
                    </p>
                    <div class="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-[var(--c-text-muted)] sm:mt-6 sm:gap-x-4 sm:text-base">
                        <span v-if="detail.score" class="text-[var(--accent)] font-700">★ {{ detail.score }}</span>
                        <span v-if="detail.year" class="flex items-center gap-1.5"><Calendar class="size-3.5" />{{ detail.year }}</span>
                        <span v-if="detail.runtime" class="hidden items-center gap-1.5 sm:flex"><Clock3 class="size-3.5" />{{ detail.runtime }}</span>
                        <span v-if="detail.category" class="hidden sm:inline">{{ detail.category }}</span>
                        <span v-if="detail.area" class="hidden sm:inline">{{ detail.area }}</span>
                    </div>
                    <p v-if="detail.description" class="line-clamp-3 mt-6 hidden max-w-2xl text-[var(--c-text-soft)] leading-8 sm:block sm:text-base">
                        {{ detail.description }}
                    </p>
                </div>

                <div class="reveal-up aspect-[2/3] w-full justify-self-end overflow-hidden border border-[var(--c-border)] rounded-[1.375rem] bg-[var(--c-poster)] shadow-[0_22px_70px_var(--c-shadow)] sm:rounded-[1.75rem]" style="animation-delay: 80ms">
                    <PosterArt :src="detail.poster || detail.backdrop" :alt="detail.title" eager :shade="false" />
                </div>
            </div>
        </section>

        <div class="grid page-shell gap-14 lg:grid-cols-[minmax(0,.8fr)_minmax(460px,1.2fr)] lg:gap-20">
            <section class="min-w-0">
                <div class="flex items-end justify-between gap-4">
                    <div>
                        <p class="mb-3 text-xs text-[var(--accent)] font-700">
                            影片信息
                        </p>
                        <h2 class="section-title">
                            影片简介
                        </h2>
                    </div>
                    <button class="min-h-11 flex shrink-0 items-center gap-2 border border-[var(--c-border-strong)] rounded-full bg-[var(--c-fill-subtle)] px-4 text-sm text-[var(--c-text)] font-600 transition hover:bg-[var(--c-fill-hover)] focus-ring" :aria-label="favorite ? '取消收藏' : '收藏'" :aria-pressed="favorite" @click="library.toggleFavorite(detail)">
                        <Bookmark class="size-4.5" :class="favorite && 'fill-current text-[var(--accent)]'" />
                        {{ favorite ? '已收藏' : '收藏' }}
                    </button>
                </div>
                <p class="mt-6 text-sm text-[var(--c-text-soft)] leading-7 sm:text-base sm:leading-8">
                    {{ detail.description || '暂无剧情简介。' }}
                </p>
                <dl class="grid grid-cols-[72px_1fr] mt-9 gap-y-4 border-t border-[var(--c-border)] pt-6 text-sm leading-6">
                    <dt class="text-[var(--c-text-muted)]">
                        导演
                    </dt><dd class="[overflow-wrap:anywhere] min-w-0 break-words text-[var(--c-text-soft)]">
                        {{ detail.director || '未知' }}
                    </dd>
                    <dt class="text-[var(--c-text-muted)]">
                        演员
                    </dt><dd class="[overflow-wrap:anywhere] min-w-0 break-words text-[var(--c-text-soft)]">
                        {{ detail.actors || '未知' }}
                    </dd>
                    <dt class="text-[var(--c-text-muted)]">
                        语言
                    </dt><dd class="[overflow-wrap:anywhere] min-w-0 break-words text-[var(--c-text-soft)]">
                        {{ detail.language || '未知' }}
                    </dd>
                </dl>
            </section>

            <section id="episodes" class="min-w-0">
                <div v-if="detail.alternatives?.length" class="mb-10">
                    <div class="mb-4 flex items-end justify-between border-b border-[var(--c-border)] pb-4">
                        <div>
                            <p class="mb-2 text-xs text-[var(--accent)] font-700">
                                播放来源
                            </p>
                            <h2 class="section-title">
                                选择线路
                            </h2>
                        </div>
                        <span class="flex items-center gap-2 text-xs text-[var(--c-text-muted)]"><RefreshCw v-if="alternativesLoading" class="size-3.5 animate-spin" />{{ detail.alternatives.length + 1 }} 个来源</span>
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <button class="max-w-full min-h-11 min-w-0 flex items-center gap-2 border border-[var(--accent)] rounded-full bg-[rgb(var(--accent-rgb)/9%)] px-4 text-sm text-[var(--accent)] font-650 focus-ring">
                            <Check class="size-4 shrink-0" /><span class="truncate">{{ detail.sourceName }}</span>
                        </button>
                        <button v-for="alternative in detail.alternatives" :key="`${alternative.sourceId}:${alternative.id}`" class="max-w-full min-h-11 min-w-0 flex items-center gap-2 border border-[var(--c-border)] rounded-full bg-[var(--c-fill-subtle)] px-4 text-sm text-[var(--c-text-soft)] transition hover:border-[var(--c-border-strong)] hover:bg-[var(--c-fill-hover)] hover:text-[var(--c-text)] focus-ring" :disabled="switching === `${alternative.sourceId}:${alternative.id}`" @click="switchSource(alternative)">
                            <RefreshCw v-if="switching === `${alternative.sourceId}:${alternative.id}`" class="size-4 shrink-0 animate-spin" /><span class="truncate">{{ alternative.sourceName }}</span><span v-if="alternative.episodeCount" class="shrink-0 text-[var(--c-text-faint)]">{{ alternative.episodeCount }} 集</span>
                        </button>
                    </div>
                </div>
                <div class="mb-6 flex items-end justify-between border-b border-[var(--c-border)] pb-4">
                    <div>
                        <p class="mb-2 text-xs text-[var(--accent)] font-700">
                            播放列表
                        </p><h2 class="section-title">
                            选集与线路
                        </h2>
                    </div>
                    <span class="text-xs text-[var(--c-text-muted)]">{{ detail.episodes.length }} 项</span>
                </div>
                <div v-if="detail.episodes.length" class="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-2 sm:grid-cols-[repeat(3,minmax(0,1fr))]">
                    <button
                        v-for="(episode, index) in detail.episodes"
                        :key="episode.id"
                        class="min-h-13 min-w-0 flex touch-manipulation items-center border border-[var(--c-border)] rounded-xl bg-[var(--c-fill-subtle)] px-3 text-left text-sm transition active:scale-[.985] hover:border-[var(--c-border-strong)] hover:bg-[var(--c-fill-hover)] sm:px-4 hover:text-[var(--c-text)] focus-ring"
                        :class="record?.episodeIndex === index ? 'border-[var(--accent)] bg-[rgb(var(--accent-rgb)/8%)] text-[var(--accent)]' : 'text-[var(--c-text-soft)]'"
                        :disabled="enteringEpisode !== undefined"
                        :title="episodeDisplayLabel(episode.label)"
                        @click="play(index)"
                    >
                        <RefreshCw v-if="enteringEpisode === index" class="mr-2 size-3.5 shrink-0 animate-spin" />
                        <span v-else class="mr-2 shrink-0 text-xs text-[var(--c-text-faint)]">{{ String(index + 1).padStart(2, '0') }}</span>
                        <span class="truncate">{{ episodeDisplayLabel(episode.label) }}</span>
                    </button>
                </div>
                <div v-else-if="refreshing" class="min-h-24 flex items-center justify-center gap-2 text-sm text-[var(--c-text-muted)]">
                    <RefreshCw class="size-4 animate-spin" />正在读取播放列表
                </div>
                <EmptyState v-else :title="error ? '播放信息暂时不可用' : '暂无可播放内容'" :description="error || '当前来源没有提供有效的播放地址。'">
                    <button v-if="error" class="mt-5 btn-ghost" @click="load()">
                        <RefreshCw class="size-4" />重新读取
                    </button>
                </EmptyState>
                <p class="mt-4 text-xs text-[var(--c-text-faint)] leading-5">
                    播放地址由浏览器直接连接来源，媒体流量不会经过 CineFlow。
                </p>
            </section>
        </div>
    </article>
</template>
